import React, { useEffect, useState } from "react"

import DropdownButton from "antd/es/dropdown/dropdown-button"
import classNames from "classnames"
import { useNavigate, useParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "shared/config/store"
import { ROUTES } from "shared/constants/routesPaths"
import { DeleteOutlined, EditOutlined, MoreOutlined } from "shared/ui"
import styled from "styled-components"
import { openModal } from 'features/common/model/slice/moadlSlice'
import { BoardToolbarAction } from 'shared/constants/toolbar'
import { ConfirmationModal } from 'features/common/ui/ConfirmationModal'

import { selectSortedChats } from "../model/selectors/aiAssistantSelectors"
import { deleteChatThunk, fetchChatsThunk } from "../model/slice/aiAssistantSlice"

export const SidebarChatsList = React.memo(() => {
  const dispatch = useAppDispatch()
  const { chatId } = useParams()
  const navigate = useNavigate()
  const chats = useAppSelector(selectSortedChats)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)

  const handleChatClick = (chatId: string) => {
    navigate(`${ROUTES.dashboard.aiAssistant}/${chatId}`)
  }

  useEffect(() => {
    dispatch(fetchChatsThunk())
  }, [dispatch])

  const handleActionClick = (chatId: string, actionId: string) => {
    if (actionId === 'rename') {
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: 'chat', data: { ids: [chatId], items: [chats.find(c => c.id === chatId)] } }))
    } else if (actionId === 'delete') {
      setSelectedChatId(chatId)
      setIsDeleteModalOpen(true)
    }
  }

  const handleDeleteConfirm = () => {
    if (selectedChatId) {
      dispatch(deleteChatThunk({ chatId: selectedChatId }))
      setIsDeleteModalOpen(false)
      setSelectedChatId(null)
      if (selectedChatId === chatId) {
        navigate(ROUTES.dashboard.aiAssistant)
      }
    }
  }

  const menuItems = [
    {
      key: 'rename',
      label: 'Rename',
      icon: <EditOutlined />,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
    },
  ]

  const getMenuProps = (chatId: string) => {
    return {
      items: menuItems,
      onClick: (e: any) => {
        e.domEvent.stopPropagation()
        handleActionClick(chatId, e.key)
      },
    }
  }

  return (
    <>
      <SContainer>
        <SDate>
          Recent chats
        </SDate>

        {chats.length === 0 && (
          <SEmptyChats>
            There are no chats
          </SEmptyChats>
        )}

        {chats.map((chat) => (
          <SChat
            key={chat.id}
            onClick={() => handleChatClick(chat.id)}
            className={classNames({ active: chatId === chat.id })}
          >
            <SChatTitle>
              {chat.title}
            </SChatTitle>

            <div className="menu-item-actions">
              <DropdownButton
                menu={getMenuProps(chat.id)}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
                buttonsRender={() => (
                  [
                    null,
                    <MoreOutlined
                      key={chat.id}
                      className="action-icon"
                      onClick={(e) => e.stopPropagation()}
                    />,
                  ]
                )}
              />
            </div>
          </SChat>
        ))}
      </SContainer>

      <ConfirmationModal
        open={isDeleteModalOpen}
        title="Delete chat"
        description="Are you sure you want to delete this chat?"
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        confirmDanger
      />
    </>
  )
})

const SContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 4px;
  max-height: calc(100vh - 190px);
  overflow: auto;
  
  @supports (-moz-appearance: none) { 
    scrollbar-color: var(--scroll-color) transparent; 
  }
  
  &::-webkit-scrollbar {
    width: 4px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-thumb {
    background-color: gray;
    border-radius: 14px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`

const SDate = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #00000073;
  margin-bottom: 5px;
  padding-left: 24px;
  height: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const SEmptyChats = styled.div`
  height: 40px;
  padding-left: 24px;
  display: flex;
  align-items: center;
  color: #00000073;
  font-size: 14px;
  font-weight: 400;
`

const SChat = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  min-height: 40px;
  padding: 0 10px 0 24px;
  color: #000000A6;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s ease-in-out;
  justify-content: space-between;

  .menu-item-actions {
    display: none;
    gap: 6px;
    transition: display 0.4s;
    flex-shrink: 0;
    overflow: visible;
  }
  
  .action-icon {
    font-size: 18px;
    color: rgba(0, 0, 0, 0.45);
    
    &:hover {
      color: var(--color-primary);
    }
  }

  &:hover, &.active {
    background-color: #0000000F;
    color: #000000E0;

    .menu-item-actions {
      display: flex;
    }
  }
`

const SChatTitle = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: calc(100% - 40px);
`